/**
 * Order detail BottomSheet — opens over Objednávky list (MS-ORD-051 modal UX).
 */

import { useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { useOptionalOverlayBackDismiss } from '../../../shared/context/OverlayBackDismissContext.js';
import { BottomSheet } from '../../../shared/ui/BottomSheet.js';
import type { PaymentHistoryItemDTO } from '../accountTypes.js';
import type { UnifiedOrderCard } from '../presenters/unifiedOrdersPresenter.js';
import { AccountOrderDetailContent } from './AccountOrderDetailContent.js';

export interface AccountOrderDetailSheetProps {
  readonly open: boolean;
  readonly transactionId: string | null;
  /** List card when found on Objednávky — subtitle + receipt context. */
  readonly listCard?: UnifiedOrderCard | null;
  readonly listReceiptRow?: PaymentHistoryItemDTO | null;
  readonly onClose: () => void;
}

export function AccountOrderDetailSheet(props: AccountOrderDetailSheetProps): JSX.Element {
  const { open, transactionId, listCard, listReceiptRow, onClose } = props;
  const { t } = useTranslation('customer');
  const titleId = useId();
  const overlayBack = useOptionalOverlayBackDismiss();
  const registerOverlayDismiss = overlayBack?.registerOverlayDismiss;

  useEffect(() => {
    if (!open || registerOverlayDismiss === undefined) {
      return;
    }
    return registerOverlayDismiss(onClose);
  }, [open, onClose, registerOverlayDismiss]);

  const title =
    transactionId !== null
      ? t('account.orders.detailTitle', { id: transactionId })
      : t('account.orders.title');

  // G2 — preserve ternary: card miss → undefined (fetch); found → receipt | null.
  // Do not collapse via `listCard?.receipt ?? null` (that made miss look like "no receipt").
  let resolvedReceiptRow: PaymentHistoryItemDTO | null | undefined = listReceiptRow;
  if (resolvedReceiptRow === undefined && listCard != null) {
    resolvedReceiptRow = listCard.receipt;
  }

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={title}
      titleId={titleId}
      closeLabel={t('account.orders.detailCloseAria')}
      testId="customer-order-detail-sheet"
      className="max-h-[90vh] bg-[var(--color-surface-elevated)] p-5 lg:max-w-2xl"
      containerClassName="lg:items-center"
    >
      {transactionId !== null ? (
        <AccountOrderDetailContent
          transactionId={transactionId}
          listCard={listCard}
          listReceiptRow={resolvedReceiptRow}
        />
      ) : null}
    </BottomSheet>
  );
}
